import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Landmark, ImagePlus, Loader2, CheckCircle2, IndianRupee } from "lucide-react";

const loadRzpScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function LoanProviderApply() {
  const [form, setForm] = useState({ name: "", company: "", contact: "", website: "", logo: "", interest_rate: "" });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo: data.url })); toast.success("Logo uploaded");
    } catch (e) { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/loan-providers/apply", form);
      if (data.razorpay && data.order_id) {
        const ok = await loadRzpScript();
        if (!ok) { toast.error("Could not load payment. Please try again."); setDone({ ...data, paid: false }); return; }
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: "INR",
          name: "My Property",
          description: "Loan provider listing fee",
          order_id: data.order_id,
          prefill: { name: form.name, contact: form.contact },
          theme: { color: "#1a5f5a" },
          handler: async (resp) => {
            try {
              await api.post(`/loan-providers/${data.provider.id}/verify-payment`, {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful!");
              setDone({ ...data, paid: true });
            } catch (e) { toast.error("Payment verification failed"); setDone({ ...data, paid: false }); }
          },
          modal: { ondismiss: () => setDone({ ...data, paid: false }) },
        });
        rzp.open();
      } else {
        setDone({ ...data, paid: false });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit, please try again");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-5 py-14 text-center" data-testid="loan-apply-success">
        <CheckCircle2 className="w-14 h-14 text-brand-emerald mx-auto" />
        <h1 className="font-heading font-bold text-2xl text-brand-ink mt-4">Application Received!</h1>
        {done.paid ? (
          <p className="text-brand-muted mt-2" data-testid="apply-paid-msg">Your payment of ₹{done.fee} was received. Our admin will review and approve your listing shortly. Approved providers appear in the Home Loans section.</p>
        ) : done.razorpay ? (
          <p className="text-brand-muted mt-2" data-testid="apply-unpaid-msg">Your application is saved but the ₹{done.fee} payment wasn't completed. Our admin will follow up, or you can re-apply to pay. Refunds, if any, are handled as per our Terms &amp; Conditions.</p>
        ) : (
          <div className="bg-card border border-brand-line rounded-2xl p-6 mt-6 text-left" data-testid="apply-upi-box">
            <div className="flex items-center gap-2 text-brand-ink font-semibold"><IndianRupee className="w-5 h-5 text-brand-emerald" /> Pay ₹{done.fee} via UPI</div>
            <p className="text-sm text-brand-muted mt-2">UPI ID: <span className="font-semibold text-brand-ink">{done.upi_id || "Contact admin"}</span></p>
            {done.upi_name && <p className="text-sm text-brand-muted">Name: {done.upi_name}</p>}
            <p className="text-xs text-brand-muted mt-3">After payment, our admin will verify and approve your listing. Approved providers appear in the Home Loans section.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-5 py-14" data-testid="loan-apply-page">
      <h1 className="font-heading font-bold text-3xl text-brand-ink flex items-center gap-2"><Landmark className="w-7 h-7 text-brand-emerald" /> List Your Loan Service</h1>
      <p className="text-brand-muted mt-2">Apply to be featured as a home-loan provider. A listing fee applies at checkout (shown on the next step).</p>
      <div className="bg-card border border-brand-line rounded-2xl p-6 mt-6 space-y-3">
        <div><Label>Your Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5" data-testid="apply-name" /></div>
        <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="mt-1.5" data-testid="apply-company" /></div>
        <div><Label>Contact Number</Label><Input value={form.contact} onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))} placeholder="919876543210" className="mt-1.5" data-testid="apply-contact" /></div>
        <div><Label>Website URL</Label><Input value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://..." className="mt-1.5" data-testid="apply-website" /></div>
        <div><Label>Interest Rate <span className="text-brand-muted text-xs">(optional)</span></Label><Input value={form.interest_rate} onChange={(e) => setForm((f) => ({ ...f, interest_rate: e.target.value }))} placeholder="from 8.5% p.a." className="mt-1.5" data-testid="apply-rate" /></div>
        <div>
          <Label>Logo</Label>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
          <div className="flex items-center gap-4 mt-2">
            {form.logo && <img src={fileUrl(form.logo)} alt="" className="w-16 h-16 rounded-lg border border-brand-line object-contain bg-white" />}
            <Button variant="outline" className="rounded-full" onClick={() => fileRef.current.click()} disabled={uploading} data-testid="apply-upload">{uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />}{form.logo ? "Change Logo" : "Upload Logo"}</Button>
          </div>
        </div>
        <Button onClick={submit} disabled={submitting} className="w-full rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover mt-2" data-testid="apply-submit">
          {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}Submit &amp; Pay
        </Button>
      </div>
    </div>
  );
}
